import React, {useEffect, useState} from 'react';
import {
    Card,
    Typography,
    Input,
    Button,
    Checkbox,
    Select,
    Space,
    Divider,
    Row,
    Col,
    Popconfirm,
    Collapse, Modal, Tooltip
} from 'antd';
import {PlusOutlined, DeleteOutlined} from '@ant-design/icons';
import {defaultServerInfo, defaultVersion} from '../../shared/utils/defaultData';
import toast from 'react-hot-toast';
import CustomDateTimePicker from "../../shared/components/date/CustomDateTimePicker";
import dayjs from "dayjs";
import {useServerInfo, useSaveServerInfo, kickAll, useReloadTime} from "./useServerInfo";
import IsLoading from "../../shared/components/IsLoading";
import ErrorPage from "../../shared/components/ErrorPage";
import {AuthContext} from "../../shared/contexts/authContext";
import {useContext} from "react";
import {REFRESH_TYPE_FUNCTION, ROLE} from "../../shared/utils/enum";
import ServerRefresh from "../../shared/components/common/ServerRefresh";
import {useCSVList} from "../../pages/csv/useCSVList";
import {useKickMessage} from "./useKickMessageManager";
import {useKrTextData} from "../probability/useItemData";
import VersionList from './VersionList';

const {Title, Text} = Typography;
const {Option} = Select;
const {Panel} = Collapse;

const ServerInfoPage = () => {
    const {data: serverInfoData, isLoading, error} = useServerInfo();
    const {data: csvList, isLoading: csvIsLoading, error: csvError} = useCSVList();
    const {data: kickMessages, isLoading: kickMessageLoading, error: kickMessageError} = useKickMessage();
    const {data: krTextData, isLoading: krTextDataLoading, error: krTextDataError} = useKrTextData();
    const {mutate: saveServer, isPending: isSaving} = useSaveServerInfo();
    const {data: reloadTimeData, isLoading: isReloadTimeData, error: reloadTimeDataError, refetch: reloadTimeDataRefetch} = useReloadTime();

    const {currentUser} = useContext(AuthContext);

    const [serverInfo, setServerInfo] = useState(defaultServerInfo);
    const [versionInput, setVersionInput] = useState(defaultVersion);
    const [newServer, setNewServer] = useState('');
    const [newPath, setNewPath] = useState('');
    const [openMarketLinks, setOpenMarketLinks] = useState(false);
    const [isKickingUsers, setIsKickingUsers] = useState(false);
    const [selectedKickMessage, setSelectedKickMessage] = useState('');
    const [modalVisible, setModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (serverInfoData) {
            setServerInfo(serverInfoData.serverInfo);
        }
    }, [serverInfoData]);

    const handleInputChange = (field, value) => {
        if (field === 'begin' || field === 'end') {
            value = Math.floor(dayjs.unix(value).set('second', 0).valueOf() / 1000);
        }
        setServerInfo(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleVersionInputChange = (field, value) => {
        setVersionInput(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleAddVersion = () => {
        if (!versionInput.version.trim() || !versionInput.csvVersion.trim()) {
            toast.error('Build Version과 CSV Version을 입력해주세요.');
            return;
        }

        const newVersion = {
            version: versionInput.version,
            csvVersion: versionInput.csvVersion,
            gameServer: versionInput.gameServer,
            csvPath: versionInput.csvPath,
            memo: versionInput.memo || '',
            isRecommendedUpdate: versionInput.isRecommendedUpdate,
        };

        setServerInfo(prev => {
            const updated = {...prev, versions: {...prev.versions}};
            if (versionInput.isAOS) {
                updated.versions.aos = [...(updated.versions.aos || []), {...newVersion}];
            }
            if (versionInput.isIOS) {
                updated.versions.ios = [...(updated.versions.ios || []), {...newVersion}];
            }
            return updated;
        });

        setVersionInput(defaultVersion);
    };

    const handleVersionChange = (platform, index, field, value) => {
        setServerInfo(prev => ({
            ...prev,
            versions: {
                ...prev.versions,
                [platform]: prev.versions[platform].map((item, i) =>
                    i === index ? {...item, [field]: value} : item
                ),
            },
        }));
    };

    const handleRemoveVersion = (platform, index) => {
        setServerInfo(prev => {
            const updated = {...prev};
            updated.versions[platform] = updated.versions[platform].filter((_, i) => i !== index);
            return updated;
        });
    };

    const handleAddItem = (field, value, resetFunc) => {
        if (!value.trim()) return;
        setServerInfo(prev => ({
            ...prev,
            [field]: [...(prev[field] || []), value.trim()],
        }));
        resetFunc('');
    };

    const handleRemoveItem = (field, idx) => {
        setServerInfo(prev => ({
            ...prev,
            [field]: prev[field].filter((_, i) => i !== idx),
        }));
    };

    const handleModalOk = async (refreshTypeFunc) => {
        setLoading(true);
        setModalVisible(false);
        await refreshTypeFunc.func();
        await reloadTimeDataRefetch();
        setLoading(false);
    };

    const handleModalCancel = () => {
        setModalVisible(false);
    };

    const handleSave = async () => {
        if (serverInfo) {
            saveServer(serverInfo, {
                onSuccess: () => {
                    toast.success('서버정보가 저장되었습니다.');
                    setModalVisible(true);
                }
            });
        }
    };

    const handleKickAllUsers = async () => {
        setIsKickingUsers(true);
        try {
            await kickAll(selectedKickMessage); // 선택된 메시지 전달
        } catch (error) {
            toast.error('유저 킥 실패: ' + error.message);
        } finally {
            setIsKickingUsers(false);
        }
    };

    if (isLoading || csvIsLoading || kickMessageLoading || krTextDataLoading || isReloadTimeData) return <IsLoading/>;
    const firstError = error || csvError || kickMessageError || krTextDataError || reloadTimeDataError;
    if (firstError) return <ErrorPage error={firstError}/>;

    return (
        <div style={{padding: '24px'}}>
            <Row gutter={16} style={{marginBottom: '24px'}}>
                <Col span={12}>
                    <Button
                        type="primary"
                        size="large"
                        block
                        onClick={handleSave}
                        loading={isSaving}
                    >
                        {isSaving ? '저장 중...' : '저장'}
                    </Button>
                </Col>
                <Col span={12}>
                    {currentUser.role >= ROLE.ADMIN && (
                        <Popconfirm
                            title="모든 유저를 킥하시겠습니까?"
                            description={
                                <div>
                                    <Select
                                        style={{width: 320}}
                                        value={selectedKickMessage}
                                        onChange={setSelectedKickMessage}
                                        placeholder="킥 메시지 선택"
                                        optionLabelProp="label"
                                    >
                                        {kickMessages?.map(msg => (
                                            <Select.Option
                                                key={msg.index}
                                                value={msg.index}
                                            >
                                                <div style={{display: 'flex', flexDirection: 'column'}}>
                                                    <span style={{fontWeight: 500}}>[{msg.index}] - {msg.memo}</span>
                                                    <span style={{color: '#888', fontSize: 13}}>
                                                        {krTextData?.[currentUser.target]?.[msg.index]?.text || ''}
                                                    </span>
                                                </div>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                    <div style={{marginTop: 12}}>
                                        <Card
                                            size="small"
                                            title="선택된 메시지"
                                            style={{background: '#f6f8fa', border: '1px solid #e0e7ef'}}
                                        >
                                            {krTextData?.[currentUser.target]?.[selectedKickMessage]?.text ? (
                                                krTextData[currentUser.target][selectedKickMessage].text
                                            ) : (
                                                <Typography.Text type="danger" strong>
                                                    !!! 테이블에 설정된 텍스트가 없습니다. !!!
                                                </Typography.Text>
                                            )}
                                        </Card>
                                    </div>
                                </div>
                            }
                            onConfirm={handleKickAllUsers}
                            okText="확인"
                            cancelText="취소"
                            disabled={isKickingUsers}
                        >
                            <Button
                                type="primary"
                                danger
                                size="large"
                                block
                                loading={isKickingUsers}
                            >
                                {isKickingUsers ? '킥 중...' : '모든 유저 킥'}
                            </Button>
                        </Popconfirm>
                    )}
                </Col>
            </Row>
            {/* Versions Section */}
            <Card style={{marginBottom: '24px'}}>
                <div>
                    <Row align="middle" justify="space-between">
                        <Col>
                            <Title level={4} style={{margin: 0}}>버전 관리</Title>
                        </Col>
                        <Col>
                            <Tooltip title="서버 정보, CSV 갱신">
                                <ServerRefresh refreshTypeFunc={REFRESH_TYPE_FUNCTION.SERVER_INFO} reloadTimeRefetch={reloadTimeDataRefetch}/>
                            </Tooltip>
                        </Col>
                    </Row>
                    <Text type="secondary">마지막 동기화 (서버정보, CSV) : {reloadTimeData.admin_table_sync_date ? reloadTimeData.admin_table_sync_date + ' (KST)' : '정보 없음'}</Text>
                </div>
                {/* 입력 폼 파트 */}
                <div style={{background: '#f6f8fa', borderRadius: 8, padding: 16, marginBottom: 24}}>
                    <Text strong style={{fontSize: 16}}>버전 추가</Text>
                    <Divider style={{margin: '8px 0 16px 0'}}/>
                    <Space wrap style={{marginBottom: 0}}>
                        <Checkbox
                            checked={versionInput.isAOS}
                            onChange={(e) => handleVersionInputChange('isAOS', e.target.checked)}
                        >
                            AOS
                        </Checkbox>
                        <Checkbox
                            checked={versionInput.isIOS}
                            onChange={(e) => handleVersionInputChange('isIOS', e.target.checked)}
                        >
                            IOS
                        </Checkbox>
                        <Input
                            placeholder="Build Version"
                            value={versionInput.version}
                            onChange={(e) => handleVersionInputChange('version', e.target.value)}
                            style={{width: 140}}
                        />
                        <Select
                            placeholder="CSV Version"
                            value={versionInput.csvVersion}
                            onChange={(value) => handleVersionInputChange('csvVersion', value)}
                            style={{width: 140}}
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                        >
                            {csvList?.map((csv) => (
                                <Option key={csv} value={csv}>{csv}</Option>
                            ))}
                        </Select>
                        <Select
                            placeholder="Game Server"
                            value={versionInput.gameServer}
                            onChange={(value) => handleVersionInputChange('gameServer', value)}
                            style={{maxWidth: 250}}
                        >
                            {serverInfo.gameServerList.map((server, idx) => (
                                <Option key={idx} value={idx}>{server}</Option>
                            ))}
                        </Select>
                        <Select
                            placeholder="CSV Path"
                            value={versionInput.csvPath}
                            onChange={(value) => handleVersionInputChange('csvPath', value)}
                            style={{maxWidth: 800}}
                        >
                            {serverInfo.csvPathList.map((path, idx) => (
                                <Option key={idx} value={idx}>{path}</Option>
                            ))}
                        </Select>
                        <Input
                            placeholder="Memo"
                            value={versionInput.memo || ''}
                            onChange={(e) => handleVersionInputChange('memo', e.target.value)}
                            style={{width: 200}}
                            maxLength={200}
                        />
                        <Button
                            type="primary"
                            icon={<PlusOutlined/>}
                            onClick={handleAddVersion}
                            disabled={!versionInput.version.trim() || !versionInput.csvVersion.trim()}
                        >
                            ADD
                        </Button>
                    </Space>
                </div>

                {/* 현재 CSV Version 선택 */}
                <div style={{marginTop: 24, display: 'flex', alignItems: 'center', gap: 12}}>
                    <Text strong style={{fontSize: 16}}>현재 CSV Version 선택</Text>
                    <Select
                        style={{width: 200}}
                        value={serverInfo.currentCsvVersion || ''}
                        onChange={value => handleInputChange('currentCsvVersion', value)}
                        showSearch
                        placeholder="CSV Version 선택"
                        optionFilterProp="children"
                        filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                    >
                        {csvList?.map(csv => (
                            <Option key={csv} value={csv}>{csv}</Option>
                        ))}
                    </Select>
                </div>

                {/* AOS 리스트 파트 */}
                <VersionList
                    platform="aos"
                    versions={serverInfo.versions.aos}
                    csvList={csvList}
                    serverList={serverInfo.gameServerList}
                    pathList={serverInfo.csvPathList}
                    handleVersionChange={handleVersionChange}
                    handleRemoveVersion={handleRemoveVersion}
                    color="#1677ff"
                    title="AOS 버전 목록"
                />
                {/* IOS 리스트 파트 */}
                <VersionList
                    platform="ios"
                    versions={serverInfo.versions.ios}
                    csvList={csvList}
                    serverList={serverInfo.gameServerList}
                    pathList={serverInfo.csvPathList}
                    handleVersionChange={handleVersionChange}
                    handleRemoveVersion={handleRemoveVersion}
                    color="#1677ff"
                    title="IOS 버전 목록"
                />
            </Card>

            <Card style={{marginBottom: '24px'}}>
                <Row align="middle" justify="space-between">
                    <Col>
                        <Title level={4} style={{margin: 0}}>Game Server List and CSV Path List</Title>
                    </Col>
                </Row>
                <div style={{marginBottom: '24px'}}>
                    <Title level={5}>Game Server List</Title>
                    <Space direction="vertical" style={{width: '100%'}}>
                        {serverInfo.gameServerList.map((server, idx) => (
                            <Row key={`server-${idx}`} gutter={8} align="middle">
                                <Col flex={1}>
                                    <Text strong>{idx}: {server}</Text>
                                </Col>
                                <Col>
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined/>}
                                        onClick={() => handleRemoveItem('gameServerList', idx)}
                                    />
                                </Col>
                            </Row>
                        ))}
                        <Row gutter={8} align="middle">
                            <Col flex={1}>
                                <Input
                                    placeholder="Game Server"
                                    value={newServer}
                                    onChange={(e) => setNewServer(e.target.value)}
                                />
                            </Col>
                            <Col>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined/>}
                                    onClick={() => handleAddItem('gameServerList', newServer, setNewServer)}
                                >
                                    ADD
                                </Button>
                            </Col>
                        </Row>
                    </Space>
                </div>

                {/* CSV Path List */}
                <div>
                    <Title level={5}>CSV Path List</Title>
                    <Space direction="vertical" style={{width: '100%'}}>
                        {serverInfo.csvPathList.map((path, idx) => (
                            <Row key={`csv-${idx}`} gutter={8} align="middle">
                                <Col flex={1}>
                                    <Text strong>{idx}: {path}</Text>
                                </Col>
                                <Col>
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined/>}
                                        onClick={() => handleRemoveItem('csvPathList', idx)}
                                    />
                                </Col>
                            </Row>
                        ))}
                        <Row gutter={8} align="middle">
                            <Col flex={1}>
                                <Input
                                    placeholder="CSV Path"
                                    value={newPath}
                                    onChange={(e) => setNewPath(e.target.value)}
                                />
                            </Col>
                            <Col>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined/>}
                                    onClick={() => handleAddItem('csvPathList', newPath, setNewPath)}
                                >
                                    ADD
                                </Button>
                            </Col>
                        </Row>
                    </Space>
                </div>
            </Card>

            {/* Server State */}
            <Card style={{marginBottom: '24px'}}>
                <Title level={4}>Server State</Title>
                <Space align="center">
                    <Button
                        type="primary"
                        size="large"
                        style={{
                            backgroundColor: serverInfo.state === 0 ? '#52c41a' : '#ff4d4f',
                            borderColor: serverInfo.state === 0 ? '#52c41a' : '#ff4d4f'
                        }}
                        onClick={() => handleInputChange('state', serverInfo.state === 0 ? 1 : 0)}
                    >
                        {serverInfo.state === 0 ? '서비스 중' : '점검 중'}
                    </Button>
                    <CustomDateTimePicker rows={serverInfo} handleSetRows={handleInputChange}/>
                </Space>
            </Card>

            {/* Market Links */}
            <Card>
                <Collapse
                    activeKey={openMarketLinks ? ['1'] : []}
                    onChange={() => setOpenMarketLinks(prev => !prev)}
                >
                    <Panel header="Market Links" key="1">
                        <Space direction="vertical" style={{width: '100%'}}>
                            <div>
                                <Text>iOS 마켓링크</Text>
                                <Input
                                    value={serverInfo.ios}
                                    onChange={(e) => handleInputChange('ios', e.target.value)}
                                    style={{marginTop: '8px'}}
                                />
                            </div>
                            <div>
                                <Text>AOS 마켓링크</Text>
                                <Input
                                    value={serverInfo.aos}
                                    onChange={(e) => handleInputChange('aos', e.target.value)}
                                    style={{marginTop: '8px'}}
                                />
                            </div>
                        </Space>
                    </Panel>
                </Collapse>
            </Card>
            {modalVisible && (
                <Modal
                    open={modalVisible}
                    title={REFRESH_TYPE_FUNCTION.SERVER_INFO.title}
                    onOk={() => handleModalOk(REFRESH_TYPE_FUNCTION.SERVER_INFO)}
                    onCancel={handleModalCancel}
                    okText="확인"
                    cancelText="취소"
                    confirmLoading={loading}
                >
                    <div style={{whiteSpace: 'pre-line'}}>
                        {REFRESH_TYPE_FUNCTION.SERVER_INFO.desc}
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default ServerInfoPage;

